import React, { useContext } from 'react';
import { ProformaNavigationGuardContext } from '../context/ProformaNavigationGuardContext';

/**
 * Breadcrumb navigation bar shown at the top of assessment pages.
 *
 * Each entry in `tupleList` is `[name, route?, onClick?]`.  The last item
 * is rendered as a static label (current page); earlier items with a `route`
 * are navigable.  Navigation goes through `guardedNavigate` from
 * `ProformaNavigationGuardContext` so that a confirmation dialog is shown when
 * the clinician has unsaved changes.  When no module page is dirty the call
 * falls straight through to React Router's `navigate()` with zero overhead.
 *
 * Optional props:
 *   rightSlot  {ReactNode}  Content rendered to the right of the crumbs, outside
 *              <nav>. Use for action buttons (e.g. appointment checklist controls)
 *              that live in the same visual row as the breadcrumb but are not
 *              navigation items.
 *   allowWrap  {boolean}  When true, crumbs can wrap to multiple lines on narrow
 *              viewports instead of being clipped by the global `.breadcrumb`
 *              `white-space: nowrap` rule. Default false preserves existing
 *              behaviour for all other proformas.
 */
function Breadcrumb({ tupleList, rightSlot, allowWrap = false }) {
  const { guardedNavigate } = useContext(ProformaNavigationGuardContext);

  const navContent = (
    <div className={`flex-grow pl-8 py-4 text-sm ${allowWrap ? 'min-w-0' : ''}`}>
      <nav className={allowWrap ? undefined : 'breadcrumb'}>
        <ul className={allowWrap
          ? 'flex flex-wrap items-center gap-x-2 gap-y-1'
          : 'flex space-x-2'
        }>
          {tupleList.map(([name, route, onClick], index) => (
            <React.Fragment key={index}>
              {index > 0 && <li>/</li>}
              {index === tupleList.length - 1 ? (
                <li>
                  <div className={`px-2 bg-purple-50 rounded-md justify-center items-center inline-flex ${
                    allowWrap ? 'h-auto min-h-5 whitespace-normal' : 'h-5'
                  }`}>
                    <div className="text-violet-900 text-sm font-semibold leading-tight">{name}</div>
                  </div>
                </li>
              ) : route ? (
                <li>
                  <button
                    onClick={(e) => {
                      if (onClick) onClick(e);
                      guardedNavigate(route);
                    }}
                    className="px-2 font-medium breadcrumb-link cursor-pointer bg-transparent border-none"
                  >
                    {name}
                  </button>
                </li>
              ) : (
                <li className="px-2 font-medium text-gray-600">
                  {name}
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </nav>
    </div>
  );

  if (!rightSlot) return navContent;

  // Match navContent's py-4 on the slot wrapper so the prefix/buttons sit on
  // the same vertical centre-line as the crumb text. items-start keeps the
  // slot aligned with the first crumb line when crumbs wrap on narrow widths.
  return (
    <div className="flex items-start">
      {navContent}
      <div className="flex-shrink-0 py-4 pr-8 flex items-center">
        {rightSlot}
      </div>
    </div>
  );
}

export default Breadcrumb;
